import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Chip, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Alert,
} from '@mui/material';
import {
  HowToReg as InquiryIcon, Search as SearchIcon, Visibility as ViewIcon, Delete as DeleteIcon,
  PersonAdd as NewIcon, PhoneForwarded as ContactedIcon, CheckCircle as AdmittedIcon,
} from '@mui/icons-material';
import { admissionAPI } from '../services/api';
import { StatCard } from './accounting/common';

const errorText = (e, fallback) => e?.response?.data?.message || fallback;
const STATUSES = ['NEW', 'CONTACTED', 'ADMITTED', 'NOT_INTERESTED'];
const statusColor = { NEW: 'warning', CONTACTED: 'info', ADMITTED: 'success', NOT_INTERESTED: 'default' };
const statusLabel = { NEW: 'New', CONTACTED: 'Contacted', ADMITTED: 'Admitted', NOT_INTERESTED: 'Not interested' };

export default function AdmissionManagement() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [detail, setDetail] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnack({ open: true, message, severity }), []);

  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([admissionAPI.getSummary(), admissionAPI.getAll()]);
      setSummary(s.data); setRows(r.data);
    } catch (e) { notify(errorText(e, 'Could not load admission inquiries'), 'error'); }
  }, [notify]);
  useEffect(() => { load(); }, [load]);

  const shown = rows
    .filter((r) => statusFilter === 'ALL' || r.status === statusFilter)
    .filter((r) => !q || `${r.fullName} ${r.phone || ''} ${r.email || ''} ${r.city || ''}`.toLowerCase().includes(q.toLowerCase()));

  const changeStatus = async (row, status) => {
    try {
      await admissionAPI.update(row.id, { status });
      notify('Status updated');
      setDetail((d) => (d && d.id === row.id ? { ...d, status } : d));
      load();
    } catch (e) { notify(errorText(e, 'Could not update status'), 'error'); }
  };

  const saveRemarks = async () => {
    try {
      await admissionAPI.update(detail.id, { status: detail.status, remarks: detail.remarks || '' });
      notify('Saved');
      setDetail(null); load();
    } catch (e) { notify(errorText(e, 'Could not save'), 'error'); }
  };

  const doDelete = async () => {
    try { await admissionAPI.delete(confirmDelete.id); notify('Inquiry deleted'); setConfirmDelete(null); load(); }
    catch (e) { notify(errorText(e, 'Could not delete'), 'error'); }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#004d40' }}>Admission Inquiries</Typography>
        <Typography variant="body2" color="text.secondary">Prospective students who submitted the admission form on the public website</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="New" value={summary?.newCount ?? 0} icon={NewIcon} gradient="linear-gradient(135deg,#ef6c00,#ffa726)" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Contacted" value={summary?.contacted ?? 0} icon={ContactedIcon} gradient="linear-gradient(135deg,#0288d1,#26c6da)" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Admitted" value={summary?.admitted ?? 0} icon={AdmittedIcon} gradient="linear-gradient(135deg,#2e7d32,#66bb6a)" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total inquiries" value={summary?.total ?? 0} icon={InquiryIcon} gradient="linear-gradient(135deg,#004d40,#00897b)" />
        </Grid>
      </Grid>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Search name, phone, email, city..." value={q} onChange={(e) => setQ(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 260 }} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['ALL', ...STATUSES].map((f) => (
                  <Chip key={f} label={f === 'ALL' ? 'All' : statusLabel[f]} clickable
                    color={statusFilter === f ? 'primary' : 'default'} onClick={() => setStatusFilter(f)} />
                ))}
              </Box>
            </Box>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Name</TableCell><TableCell>Phone</TableCell><TableCell>Program</TableCell>
                <TableCell>City</TableCell><TableCell>Submitted</TableCell><TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shown.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.fullName}
                      {r.email ? <Typography variant="caption" display="block" color="text.secondary">{r.email}</Typography> : null}
                    </TableCell>
                    <TableCell>{r.phone}</TableCell>
                    <TableCell>{r.program || '-'}</TableCell>
                    <TableCell>{r.city || '-'}</TableCell>
                    <TableCell>{r.submittedAt ? new Date(r.submittedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                    <TableCell>
                      <TextField select size="small" value={r.status} onChange={(e) => changeStatus(r, e.target.value)}
                        sx={{ minWidth: 150 }}
                        SelectProps={{ renderValue: (v) => <Chip size="small" label={statusLabel[v]} color={statusColor[v]} /> }}>
                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel[s]}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View details"><IconButton size="small" color="primary" onClick={() => setDetail(r)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setConfirmDelete(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {shown.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>No inquiries for this filter</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#004d40', color: 'white' }}>Inquiry details</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {detail && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Full name</Typography><Typography sx={{ fontWeight: 600 }}>{detail.fullName}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Phone</Typography><Typography sx={{ fontWeight: 600 }}>{detail.phone}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Email</Typography><Typography>{detail.email || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Program interested</Typography><Typography>{detail.program || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Qualification</Typography><Typography>{detail.qualification || '-'}</Typography></Grid>
              <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">City</Typography><Typography>{detail.city || '-'}</Typography></Grid>
              <Grid item xs={12}><Typography variant="caption" color="text.secondary">Message</Typography><Typography>{detail.message || '-'}</Typography></Grid>
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label="Status" value={detail.status}
                  onChange={(e) => setDetail({ ...detail, status: e.target.value })}>
                  {STATUSES.map((s) => <MenuItem key={s} value={s}>{statusLabel[s]}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} size="small" label="Follow-up remarks" value={detail.remarks || ''}
                  onChange={(e) => setDetail({ ...detail, remarks: e.target.value })} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetail(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveRemarks}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete inquiry?</DialogTitle>
        <DialogContent><Typography>Remove the inquiry from {confirmDelete?.fullName}? This cannot be undone.</Typography></DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}
